import { useRef, useState } from "react";
import type { Member, MemberAccountTransaction, PaymentMode } from "@/services/types";
import { memberService } from "@/services/members";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Select } from "@/components/base/Select";
import { TextField } from "@/components/base/TextField";
import { Textarea } from "@/components/base/Textarea";
import { DatePicker } from "@/components/base/DatePicker";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { useToast } from "@/context/ToastContext";

interface AdvanceDepositModalProps {
  open: boolean;
  onClose: () => void;
  member: Member;
  onSuccess: (transaction: MemberAccountTransaction, updatedMember: Member) => void;
}

const DEPOSIT_MODES: { value: PaymentMode; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
];

function todayDateOnly(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function AdvanceDepositModal({ open, onClose, member, onSuccess }: AdvanceDepositModalProps) {
  const toast = useToast();
  const [amount, setAmount] = useState<number | null>(null);
  const [mode, setMode] = useState<PaymentMode>("cash");
  const [referenceNo, setReferenceNo] = useState("");
  const [receivedOn, setReceivedOn] = useState<string>(todayDateOnly());
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const referenceNeeded = mode !== "cash";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!amount || amount <= 0) {
      setError("Please enter a valid deposit amount greater than zero.");
      return;
    }
    if (referenceNeeded && !referenceNo.trim()) {
      setError("Reference number is required for non-cash deposits.");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const res = await memberService.depositAdvance(member.id, {
        amount,
        mode,
        referenceNo: referenceNo.trim() || undefined,
        receivedOn,
        remarks: remarks.trim() || undefined,
      });

      toast.success("Deposit successful", `₹${amount.toLocaleString("en-IN")} credited to ${member.fullName}'s account.`);
      onSuccess(res.transaction, res.member);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not process advance deposit.");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Deposit Advance Money"
      description={`Add funds into ${member.fullName}'s advance wallet balance.`}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<i className="ri-check-line" />}
            loading={loading}
            onClick={handleSubmit}
          >
            Confirm Deposit
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Current Balance Banner */}
        {(() => {
          const currentBal = Number(member.accountBalance ?? 0);
          const isNegative = currentBal < 0;
          const depositVal = amount ?? 0;
          const projectedBal = currentBal + depositVal;

          return (
            <div className={`rounded-lg p-3 text-sm space-y-1.5 ${isNegative ? "bg-amber-50 border border-amber-200" : "bg-background-100/70"}`}>
              <div className="flex items-center justify-between">
                <span className={isNegative ? "text-amber-800 font-medium" : "text-foreground-600"}>
                  {isNegative ? "Current Wallet Deficit (Overdraft)" : "Current Account Balance"}
                </span>
                <span className={`font-semibold ${isNegative ? "text-danger-600 font-bold" : "text-foreground-950"}`}>
                  <CurrencyDisplay amount={currentBal} />
                </span>
              </div>
              {isNegative && depositVal > 0 ? (
                <div className="flex items-center justify-between pt-1 border-t border-amber-200/60 text-xs">
                  <span className="text-amber-700">Balance after deposit:</span>
                  <span className={`font-bold ${projectedBal < 0 ? "text-danger-600" : projectedBal === 0 ? "text-foreground-900" : "text-success-700"}`}>
                    <CurrencyDisplay amount={projectedBal} />
                    {projectedBal >= 0 ? " (Deficit Cleared)" : " (Remaining Deficit)"}
                  </span>
                </div>
              ) : null}
            </div>
          );
        })()}

        <div>
          <CurrencyInput
            label="Deposit Amount"
            value={amount}
            onChange={setAmount}
            placeholder="0.00"
            required
            autoFocus
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Payment Mode"
            value={mode}
            onChange={(v) => setMode(v as PaymentMode)}
            options={DEPOSIT_MODES}
          />
          <TextField
            label={referenceNeeded ? "Reference Number" : "Reference (Optional)"}
            placeholder={referenceNeeded ? "e.g. UPI-123456" : "Optional"}
            value={referenceNo}
            onChange={(e) => setReferenceNo(e.target.value)}
          />
        </div>

        <DatePicker label="Deposit Date" value={receivedOn} onChange={setReceivedOn} />

        <Textarea
          label="Remarks (Optional)"
          placeholder="e.g. Advance paid for cycles 2 & 3"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={2}
        />

        {error ? <p className="text-xs text-danger-600">{error}</p> : null}

        <div className="rounded-md bg-emerald-50/70 p-2.5 text-xs text-emerald-800">
          <i className="ri-information-line mr-1 text-emerald-600" />
          An official Advance Receipt (ADV-*) will be generated upon confirmation and can be printed immediately.
        </div>
      </form>
    </Modal>
  );
}
